/**
 * Compact Position Tracker alert-control pull-up (#234).
 *
 * The trader's GLOBAL policy for the alerts they tune while watching orders:
 * sound and flash delivery plus the personal level for every channel that
 * declares a delivery threshold. Settings stays the complete canonical home
 * (master volume, fill/order/boot sounds, per-alert volume, explanatory copy)
 * and is one click away from the footer link. The per-order velocity bell
 * (`cardKit.VelocityMuteToggle`) is untouched — it remains the tactical
 * override that sits on top of this policy.
 *
 * PROPS-ONLY, BY CONTRACT. This component imports no store and calls no hook.
 * The Position Tracker takes ONE `useAlertPreferences()` snapshot for the whole
 * view (`index.tsx:1346`, and the rule restated at `:2597-2600`) and threads it
 * down. A second hook instance would register a second `storage` listener and a
 * second `subscribeAlertPreferences` subscription on the app's heaviest view —
 * exactly what the issue's "no duplicate listeners" line forbids — and would
 * put a second React state cell for delivery settings inside the tree that
 * comment governs.
 *
 * NO DERIVED CROSS-CHANNEL COPY. Storage may legitimately hold an inverted
 * ordered pair: #233 keeps storage lossless and repairs the order at each
 * CONSUMPTION site, not on read. #232's adversarial review caught a panel
 * printing such a pair raw and announcing levels the tones did not fire at.
 * Every field here shows only its own channel's stored number — an editor, not
 * a claim — so that whole bug class is structurally unreachable. The single
 * exception quotes a CONSTANT (the divergence chime), never a stored value.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { BellRing, ExternalLink, X } from 'lucide-react';
import { useDismissibleLayer } from '@/hooks/ui/useDismissibleLayer';
import { AlertSwitch, AlertThresholdField, CATEGORY_LABELS } from '@/components/molecules/alertControls';
import {
  ALERT_REGISTRY,
  THRESHOLD_ALERT_CHANNEL_IDS,
  type AlertCategory,
  type AlertChannelId,
  type AlertChannelSettings,
} from '@/lib/alerts/registry';
import { DIVERGENCE_THRESHOLD_CENTS } from '@/lib/divergence/polyKalshiDivergence';

/** Kept in sync with the panel's `w-[340px]`; the right-anchor math needs it. */
const PANEL_WIDTH = 340;
/** Smallest gap the panel keeps from every viewport edge. */
const VIEWPORT_MARGIN = 8;
/** Floor for the computed max height, so a tiny window still shows something. */
const MIN_PANEL_HEIGHT = 160;

/** Group order follows the registry's own order, never a hand-written list. */
const CATEGORY_ORDER: AlertCategory[] = THRESHOLD_ALERT_CHANNEL_IDS.reduce<AlertCategory[]>(
  (acc, id) => {
    const c = ALERT_REGISTRY[id].category;
    return acc.includes(c) ? acc : [...acc, c];
  },
  []
);

export function AlertControlsMenu({
  channels,
  onSoundEnabled,
  onVisualEnabled,
  onDeliveryThreshold,
}: {
  /** The view's single alert-preference snapshot. Read-only here. */
  channels: Readonly<Record<AlertChannelId, AlertChannelSettings>>;
  onSoundEnabled: (id: AlertChannelId, enabled: boolean) => void;
  onVisualEnabled: (id: AlertChannelId, enabled: boolean) => void;
  /** May persist a DIFFERENT value than passed — bounds + sibling clamp. */
  onDeliveryThreshold: (id: AlertChannelId, threshold: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, maxHeight: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Anchored under the trigger and right-aligned, then clamped into the
  // viewport on ALL FOUR sides. The panel is `position: fixed`, so anything
  // pushed past an edge is unreachable forever — no scroll can bring it back.
  //
  //  - top: the anchor sits in a toolbar that scrolls away; following it past 0
  //    slid the panel's own header, and its close button, off the screen.
  //  - bottom: `maxHeight` is computed from the space actually left below the
  //    panel rather than a fixed `70vh`, so a low trigger or a short window
  //    scrolls the panel's body instead of amputating it.
  //  - left/right: right-anchored to the trigger, but never past either edge.
  //
  // `setPosition` bails out when nothing moved. The capture-phase scroll
  // listener below fires for EVERY scrolling element in the app, and a fresh
  // object literal each time would defeat React's `Object.is` bailout and
  // re-render the whole panel on scrolls that did not move it at all.
  const reposition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rightLimit = Math.max(VIEWPORT_MARGIN, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN);
    const left = Math.min(Math.max(VIEWPORT_MARGIN, rect.right - PANEL_WIDTH), rightLimit);

    // Below the trigger by default. When too little room is left down there,
    // the panel slides UP to sit on the bottom margin rather than keeping its
    // anchor and overflowing — a floor on `maxHeight` alone would have pushed
    // the surplus off a `fixed` element, where nothing can scroll it back.
    let top = Math.max(VIEWPORT_MARGIN, rect.bottom + 6);
    if (window.innerHeight - top - VIEWPORT_MARGIN < MIN_PANEL_HEIGHT) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - VIEWPORT_MARGIN - MIN_PANEL_HEIGHT);
    }
    const maxHeight = window.innerHeight - top - VIEWPORT_MARGIN;
    setPosition((prev) =>
      prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight }
    );
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // The anchor lives in a sticky toolbar that deliberately UN-PINS at the end
  // of the positions region (`index.tsx:3681-3687`), so the trigger moves while
  // the panel is open. Capture phase, because the scrolling element is the
  // view's own container, not the window.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);

  // Shared dismissal. Escape returns focus via close() (which focuses the
  // trigger); mousedown and focusout call setOpen(false) without moving focus.
  // dismissOnFocusOut keeps this panel from ever being a background layer
  // behind a modal — see Design Decision 1 in the #336 plan.
  useDismissibleLayer({
    open,
    onDismiss: (reason) => {
      if (reason === 'escape') close();
      else setOpen(false);
    },
    refs: [triggerRef, panelRef],
    dismissOnFocusOut: true,
  });

  // The portal appends the panel at the end of the body, so tabbing from the
  // trigger would otherwise walk the rest of the toolbar first.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Position Tracker alert controls"
        title="Alert controls — queue, velocity and cross-venue levels"
        // p-1.5 + a 14px icon is the refresh button's exact box, so mounting
        // this cannot grow the header row (the discipline at `index.tsx:1002`).
        // Never tinted by preference state: the launcher is passive until
        // opened, and it stays muted rather than taking the per-order bell's
        // amber — colour is what separates global policy from tactical override.
        className={`rounded p-1.5 transition-colors ${
          open ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
        }`}
      >
        <BellRing size={14} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          // Portalled and z-[100] because the anchoring toolbar carries
          // `backdrop-blur` — a non-none backdrop-filter creates a stacking
          // context AND a containing block, so an inline absolute panel would
          // be confined to the toolbar's z-30 and paint UNDER the z-40 pinned
          // block. The portal changes no existing z-index or sticky class.
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="false"
            aria-label="Position Tracker alert controls"
            tabIndex={-1}
            style={{ top: position.top, left: position.left, maxHeight: position.maxHeight }}
            className="border-border bg-card fixed z-[100] w-[340px] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border p-3 shadow-lg focus:outline-none"
          >
            <div className="mb-2 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-foreground text-xs font-semibold">Alert controls</h2>
                <p className="text-muted-foreground text-[11px] leading-snug">
                  Your levels for queue, velocity and cross-venue alerts.
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                aria-label="Close alert controls"
                className="text-muted-foreground hover:bg-muted hover:text-foreground focus:ring-primary/50 shrink-0 rounded p-1 transition-colors focus:ring-2 focus:outline-none"
              >
                <X size={13} aria-hidden="true" />
              </button>
            </div>

            {/* Column captions. Purely visual — every switch carries its own
                aria-label, so a screen reader never depends on these. */}
            <div className="text-muted-foreground/70 mb-1 flex items-center justify-end gap-2 pr-0.5 text-[10px] font-medium tracking-wider uppercase">
              <span className="w-9 text-center">Sound</span>
              <span className="w-9 text-center">Flash</span>
            </div>

            <div className="space-y-3">
              {CATEGORY_ORDER.map((category) => (
                <section key={category} aria-label={CATEGORY_LABELS[category]}>
                  <h3 className="text-muted-foreground mb-1.5 text-[10px] font-medium tracking-wider uppercase">
                    {CATEGORY_LABELS[category]}
                  </h3>
                  <div className="space-y-1.5">
                    {THRESHOLD_ALERT_CHANNEL_IDS.filter(
                      (id) => ALERT_REGISTRY[id].category === category
                    ).map((id) => (
                      <AlertControlRow
                        key={id}
                        id={id}
                        settings={channels[id]}
                        channels={channels}
                        onSoundEnabled={onSoundEnabled}
                        onVisualEnabled={onVisualEnabled}
                        onDeliveryThreshold={onDeliveryThreshold}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Direction-free on purpose. Naming which of a pair is the lower
                one would be a derived claim about a pair storage may hold
                inverted — the one thing this panel refuses to render. */}
            <p className="text-muted-foreground/80 mt-3 text-[11px] leading-snug">
              Paired levels are kept apart automatically — editing one may nudge it clear of its
              partner.
            </p>

            <Link
              to="/settings#alerts"
              onClick={() => setOpen(false)}
              className="focus:ring-primary/50 mt-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] text-blue-500 transition-colors hover:bg-blue-500/10 focus:ring-2 focus:outline-none dark:text-blue-400"
            >
              <ExternalLink size={11} aria-hidden="true" />
              All alerts, master volume &amp; sounds in Settings
            </Link>
          </div>,
          document.body
        )}
    </>
  );
}

function AlertControlRow({
  id,
  settings,
  channels,
  onSoundEnabled,
  onVisualEnabled,
  onDeliveryThreshold,
}: {
  id: AlertChannelId;
  settings: AlertChannelSettings;
  channels: Readonly<Record<AlertChannelId, AlertChannelSettings>>;
  onSoundEnabled: (id: AlertChannelId, enabled: boolean) => void;
  onVisualEnabled: (id: AlertChannelId, enabled: boolean) => void;
  onDeliveryThreshold: (id: AlertChannelId, threshold: number) => void;
}) {
  const meta = ALERT_REGISTRY[id];

  return (
    <div className="bg-muted/50 rounded-md px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <span className="text-foreground min-w-0 text-[11px] leading-tight font-medium">
          {meta.label}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {/* The velocity channels have NO tone (registry: sound=false). An
              empty slot holding the column, not a disabled switch — there is
              nothing to enable, and a dead control is what #232 banned. */}
          <span className="flex w-9 justify-center">
            {meta.sound ? (
              <AlertSwitch
                checked={settings.soundEnabled}
                label={meta.label}
                kind="sound"
                onChange={(next) => onSoundEnabled(id, next)}
              />
            ) : (
              <span className="text-muted-foreground/50 text-[11px]" aria-hidden="true">
                —
              </span>
            )}
          </span>
          {/* Gated on the capability for the same reason as sound, even though
              every channel in today's row source declares it. Nothing in the
              registry enforces `threshold !== null => visual`, and the store
              FORCES `visualEnabled` false wherever `meta.visual` is false — so
              an ungated switch on a future sound-only tunable channel would
              snap back off at every click. */}
          <span className="flex w-9 justify-center">
            {meta.visual ? (
              <AlertSwitch
                checked={settings.visualEnabled}
                label={meta.label}
                kind="flash"
                onChange={(next) => onVisualEnabled(id, next)}
              />
            ) : (
              <span className="text-muted-foreground/50 text-[11px]" aria-hidden="true">
                —
              </span>
            )}
          </span>
        </div>
      </div>

      {/* `normalizeDeliveryChannels` guarantees a number wherever the meta
          declares bounds, so the null arm is unreachable for these rows. It
          renders NOTHING rather than substituting `meta.defaults`: an editor
          must never show a number the store does not hold. */}
      {meta.threshold !== null && settings.deliveryThreshold !== null && (
        <div className="mt-1.5">
          <AlertThresholdField
            id={id}
            label={meta.label}
            value={settings.deliveryThreshold}
            meta={meta.threshold}
            channels={channels}
            onCommit={(next) => onDeliveryThreshold(id, next)}
          />
        </div>
      )}

      {/* The ONE claim in this panel, and it quotes a CONSTANT. The divergence
          emit site runs two latches: a canonical fixed-2¢ one driving the CHIME
          and a personal one driving only the FLASH. Without this a trader who
          set 10¢ would read the field above as the chime level. */}
      {id === 'divergence' && (
        <p className="text-muted-foreground/80 mt-1 text-[10px] leading-snug">
          Sets the flash only — the chime always rings at {DIVERGENCE_THRESHOLD_CENTS}¢.
        </p>
      )}
    </div>
  );
}
